/// <amd-module name="Repos/Views/FilesHubActions/Components/NewFileDialog" />
import * as React from "react";
import { getInvalidFileNameMessage, getParentPath } from "Repos/Common/Util/File";
import { IFilesHubViewService } from "Repos/Views/FilesHub/FilesHubViewService";
import * as Resources from "Repos/Views/FilesHubActions/Resources";
import { ObservableValue } from "VSS/Core/Observable";
import { equals, format } from "VSS/Core/Util/String";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { combineUrlPaths } from "VSS/Platform/Util/Url";
import { IButtonProps } from "VSSUI/Button";
import { Dialog } from "VSSUI/Dialog";
import { FormItem } from "VSSUI/FormItem";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { TextField } from "VSSUI/TextField";
import { Tooltip } from "VSSUI/TooltipEx";
import { KeyCode } from "VSSUI/Util";

export interface INewFileDialogProps {
    createFolder?: boolean;
    onDismiss: (name?: string) => void;
    parentPath: string;
}

export class NewFileDialog extends VssComponent<INewFileDialogProps> {
    private message = new ObservableValue<string>("");
    private name = new ObservableValue<string>("");
    private nameMessage = new ObservableValue<string>(Resources.NewFileNameDefaultMessage);
    private nameMessageIsError = new ObservableValue<boolean>(false);
    private folderFilename = new ObservableValue<string>("");
    private folderFilenameMessage = new ObservableValue<string>(Resources.NewFolderDefaultFileMessage);
    private folderFilenameMessageIsError = new ObservableValue<boolean>(false);
    private fieldsValid = new ObservableValue<boolean>(false);
    private filesService: IFilesHubViewService;

    constructor(props: INewFileDialogProps, context: IVssComponentContext) {
        super(props);

        this.filesService = context.pageContext.getService<IFilesHubViewService>("IFilesHubViewService");
    }

    public render() {
        const { createFolder } = this.props;

        let parentPath = this.props.parentPath;
        if (!parentPath.endsWith("/")) {
            parentPath += "/";
        }

        return (
            <Observer valid={this.fieldsValid}>
                {(props: { valid: boolean }) => {
                    const { onDismiss } = this.props;
                    const buttons: IButtonProps[] = [
                        {
                            onClick: () => {
                                onDismiss();
                            },
                            text: Resources.Cancel
                        },
                        {
                            onClick: this.submit,
                            disabled: !props.valid,
                            primary: true,
                            text: Resources.Create
                        }
                    ];
                    return (
                        <Dialog
                            defaultActiveElement={".repos-new-file-dialog-path-input"}
                            onDismiss={() => {
                                onDismiss();
                            }}
                            titleProps={{ text: createFolder ? Resources.NewFolder : Resources.NewFile }}
                            footerButtonProps={buttons}
                        >
                            <div className="rhythm-vertical-16 flex-grow">
                                <Observer message={this.message}>
                                    {(messageProps: { message: string }) => {
                                        return (
                                            messageProps.message && (
                                                <MessageCard severity={props.valid ? MessageCardSeverity.Warning : MessageCardSeverity.Error}>
                                                    {messageProps.message}
                                                </MessageCard>
                                            )
                                        );
                                    }}
                                </Observer>
                                <FormItem
                                    label={createFolder ? Resources.NewFolderName : Resources.NewFileName}
                                    error={this.nameMessageIsError}
                                    message={this.nameMessage}
                                >
                                    <TextField
                                        onChange={this.onNameChanged}
                                        onKeyDown={this.onKeyDown}
                                        prefixIconProps={{
                                            render: () => (
                                                <span className="repos-new-file-dialog-path-prefix flex-row flex-center">
                                                    <Tooltip overflowOnly={true} text={parentPath}>
                                                        <span className="text-ellipsis">{parentPath}</span>
                                                    </Tooltip>
                                                </span>
                                            )
                                        }}
                                        value={this.name}
                                        inputClassName="repos-new-file-dialog-path-input"
                                    />
                                </FormItem>
                                {createFolder && (
                                    <FormItem
                                        className="repos-new-file-dialog-folder-file-name"
                                        label={Resources.NewFileName}
                                        error={this.folderFilenameMessageIsError}
                                        message={this.folderFilenameMessage}
                                    >
                                        <TextField onChange={this.onFolderFilenameChanged} onKeyDown={this.onKeyDown} value={this.folderFilename} />
                                    </FormItem>
                                )}
                            </div>
                        </Dialog>
                    );
                }}
            </Observer>
        );
    }

    private submit = (): void => {
        let name = this.name.value;
        if (this.props.createFolder) {
            name = combineUrlPaths(name, this.folderFilename.value);
        }
        this.props.onDismiss(name);
    };

    private onKeyDown = (ev: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        if (ev.which === KeyCode.enter && this.fieldsValid.value) {
            this.submit();
        }
    };

    private onNameChanged = (ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, name: string): void => {
        this.name.value = name;

        let message = Resources.NewFileNameDefaultMessage;
        let messageIsError = false;

        const trimmed = name.trim();
        if (!trimmed || trimmed.endsWith("/")) {
            message = Resources.NewFileNameCannotBeEmpty;
            messageIsError = true;
        } else {
            const parts = name.split("/");
            let path = "/";
            for (const part of parts) {
                if (!part.trim()) {
                    message = format(Resources.NewFileFolderNameEmptyFormat, path);
                    messageIsError = true;
                    break;
                }

                const invalidNameMessage = getInvalidFileNameMessage(part);
                if (invalidNameMessage) {
                    message = invalidNameMessage;
                    messageIsError = true;
                    break;
                }
                path += part + "/";
            }
        }

        this.nameMessage.value = message;
        this.nameMessageIsError.value = messageIsError;

        this.updateFieldsValid();
    };

    private onFolderFilenameChanged = (ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, name: string): void => {
        this.folderFilename.value = name;

        let message = Resources.NewFolderDefaultFileMessage;
        let messageIsError = false;

        name = name.trim();
        if (!name || name.endsWith("/")) {
            message = Resources.NewFileNameCannotBeEmpty;
            messageIsError = true;
        } else {
            const invalidNameMessage = getInvalidFileNameMessage(name) || "";
            if (invalidNameMessage) {
                message = invalidNameMessage;
                messageIsError = true;
            }
        }

        this.folderFilenameMessage.value = message;
        this.folderFilenameMessageIsError.value = messageIsError;

        this.updateFieldsValid();
    };

    private updateFieldsValid() {
        let valid = !!this.name.value.trim() && !this.nameMessageIsError.value;
        if (valid && this.props.createFolder) {
            valid = !!this.folderFilename.value.trim() && !this.folderFilenameMessageIsError.value;
        }

        this.message.value = "";
        if (valid) {
            const path = combineUrlPaths(this.props.parentPath, this.name.value);

            const parentPath = getParentPath(path)!;
            const existingItems = this.filesService.getChildItems(parentPath);

            const matchingItems = existingItems.value.filter(i => equals(i.path, path, true));
            if (matchingItems.length) {
                if (matchingItems.some(i => equals(i.path, path, false))) {
                    this.message.value = format(Resources.NewFileAlreadyExists, path);
                    valid = false;
                } else {
                    this.message.value = format(Resources.NewFileOnlyDiffCase, path);
                }
            }
        }

        if (this.fieldsValid.value !== valid) {
            this.fieldsValid.value = valid;
        }
    }
}
